/**
 * Courbe de temps de roulage — EF-CAL-009, étape 1.
 *
 * `T[i]` est le temps de roulage cumulé nécessaire pour atteindre le point `i`
 * de la trace de calcul. La vitesse étant minorée par la vitesse minimale, qui
 * est strictement positive, `T` est strictement croissante, donc inversible.
 *
 * `T` ne dépend que de la trace de calcul et des quatre vitesses de EF-CAL-001.
 * Elle est conservée tant que ni l'une ni les autres ne changent, et n'est donc
 * pas reconstruite lorsque seuls les paramètres d'arrêt sont modifiés.
 */

import { kmhToMs } from '../core/units';
import type { SpeedParameters } from '../core/parameters';
import { progressionAt, type ComputedTrack } from '../geo/track';
import { speedAtGrade } from './speed-profile';

export interface RidingTimeCurve {
  /** Temps de roulage cumulé, en secondes réelles. */
  readonly seconds: Float64Array;
  readonly count: number;
  readonly totalSeconds: number;
  readonly lengthMeters: number;
  /** Progression de chaque point, recopiée pour éviter une seconde indirection. */
  readonly progression: Float64Array;
}

function readAt(array: Float64Array, index: number): number {
  const value = array[index];
  if (value === undefined) throw new Error(`Indice hors tableau : ${index}`);
  return value;
}

/**
 * Construit la courbe `T`. Lorsque l'altitude est indisponible au sens de
 * EF-GPX-003, les temps sont calculés avec la vitesse sur le plat sur la
 * totalité du parcours.
 */
export function buildRidingTimeCurve(track: ComputedTrack, speed: SpeedParameters): RidingTimeCurve {
  const count = track.count;
  const seconds = new Float64Array(count);
  const progression = new Float64Array(count);
  const useGrades = track.elevation.available;

  progression[0] = count > 0 ? progressionAt(track, 0) : 0;

  for (let i = 1; i < count; i += 1) {
    const from = readAt(track.cumulativeDistance, i - 1);
    const to = readAt(track.cumulativeDistance, i);
    progression[i] = to;
    const span = to - from;
    const grade = useGrades ? readAt(track.grade, i) : 0;
    const speedMs = kmhToMs(speedAtGrade(speed, grade));
    seconds[i] = readAt(seconds, i - 1) + (speedMs > 0 ? span / speedMs : 0);
  }

  return {
    seconds,
    progression,
    count,
    totalSeconds: count > 0 ? readAt(seconds, count - 1) : 0,
    lengthMeters: track.lengthMeters,
  };
}

/** Temps de roulage nécessaire pour atteindre une progression donnée. */
export function ridingTimeAt(curve: RidingTimeCurve, progressionMeters: number): number {
  if (curve.count === 0) return 0;
  const clamped = Math.min(curve.lengthMeters, Math.max(0, progressionMeters));
  const index = indexBefore(curve.progression, curve.count, clamped);
  if (index >= curve.count - 1) return curve.totalSeconds;

  const from = readAt(curve.progression, index);
  const to = readAt(curve.progression, index + 1);
  const span = to - from;
  const ratio = span > 0 ? (clamped - from) / span : 0;
  const timeFrom = readAt(curve.seconds, index);
  const timeTo = readAt(curve.seconds, index + 1);
  return timeFrom + (timeTo - timeFrom) * ratio;
}

/** Progression atteinte après un temps de roulage donné. Réciproque exacte. */
export function progressionAtRidingTime(curve: RidingTimeCurve, ridingSeconds: number): number {
  if (curve.count === 0) return 0;
  if (ridingSeconds <= 0) return 0;
  if (ridingSeconds >= curve.totalSeconds) return curve.lengthMeters;

  const index = indexBefore(curve.seconds, curve.count, ridingSeconds);
  if (index >= curve.count - 1) return curve.lengthMeters;

  const from = readAt(curve.seconds, index);
  const to = readAt(curve.seconds, index + 1);
  const span = to - from;
  const ratio = span > 0 ? (ridingSeconds - from) / span : 0;
  const progressionFrom = readAt(curve.progression, index);
  const progressionTo = readAt(curve.progression, index + 1);
  return progressionFrom + (progressionTo - progressionFrom) * ratio;
}

/** Plus grand indice dont la valeur est inférieure ou égale à la cible. */
function indexBefore(values: Float64Array, count: number, target: number): number {
  let low = 0;
  let high = count - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (readAt(values, middle) <= target) low = middle;
    else high = middle - 1;
  }
  return low;
}
